
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// Services
import ErrorService from '@/utils/ErrorService';

// Api
import { getAllCities } from '@/utils/api/Cities';
import { getAllOrganisations } from '@/utils/api/Organisations';
import { getAllProjects } from '@/utils/api/Projects';
import { getAllRoles } from '@/utils/Api';

// Components
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import VAdministrationForm from '@/components/Administration/VAdministrationForm.vue';

import VInput from '@/components/VInput.vue';
import VSelect from '@/components/VSelect.vue';
import VButton from '@/components/VButton/VButton.vue';
import VFormField from '@/components/VFormField.vue';

// Interfaces
import User from '@/ship/Models/User';
import Role from '@/ship/Models/Role';
import City from '@/ship/Models/City';
import Project from '@/ship/Models/Project';
import Organisation from '@/ship/Models/Organisation';
import UserEditModel from '@/ship/Models/UserEditModel';

type FilteredCity = Pick<City, 'id' | 'name'>;
type FilteredRole = Pick<Role, 'id' | 'displayName'>;
type FilteredProject = Pick<Project, 'id' | 'project'>;
type FilteredOrganisation = Pick<Organisation, 'id' | 'shortName'>;

@Component({
    components: {
        ValidationObserver,
        ValidationProvider,

        VAdministrationForm,

        VFormField,
        VInput,
        VSelect,
        VButton,
    },
})
export default class VAdministrationUserForm extends Vue {
    // If the user property exists, it means that the form is updating user, not creating it
    @Prop({ type: Object, default: null })
    public user!: User | null;

    @Prop({ type: String, required: true })
    public submitLabel!: string;

    @Prop({ type: Boolean, default: false })
    public isLoading!: boolean;

    public roles: FilteredRole[] | null = null;
    public cities: FilteredCity[] | null = null;
    public citiesDashboard: FilteredCity[] | null = null;
    public organisations: FilteredOrganisation[] | null = null;
    public projects: FilteredProject[] | null = null;

    public model: UserEditModel = {
        email: null,
        firstName: null,
        surname: null,
        patronymic: null,
        cities: [],
        citiesDashboard: null,
        projects: [],
        roles: [],
        organisations: [],
    };

    public get isUpdating() {
        return this.user;
    }

    @Watch('user')
    public userWatchHandler() {
        if (this.user) {
            const { email, firstName, surname, patronymic, cities, citiesDashboard, projects, roles, organisationIds } =
                this.user;

            this.model = {
                email: email ?? '',
                firstName,
                surname,
                patronymic,
                cities: cities?.data.map(({ id }) => id) ?? [],
                citiesDashboard: citiesDashboard?.data[0]?.id ?? null,
                projects: projects?.data.map(({ id }) => id) ?? [],
                roles: roles?.data.map(({ id }) => id) ?? [],
                organisations: organisationIds,
            };
        }
    }

    public handleSubmit() {
        this.$emit('submit', this.model);
    }

    public async getCities() {
        const response = await getAllCities<FilteredCity>({ filter: 'id;name', orderBy: 'name', limit: 0 }).catch(
            ErrorService.handleApiError,
        );
        return response ? response.data.data : [];
    }

    public async getRoles() {
        const response = await getAllRoles<FilteredRole>({
            filter: 'id;display_name',
            orderBy: 'display_name',
            limit: 0,
        }).catch(ErrorService.handleApiError);
        return response ? response.data.data : [];
    }

    public async getProjects() {
        const response = await getAllProjects<FilteredProject>({
            filter: 'id;project',
            orderBy: 'project',
            limit: 0,
        }).catch(ErrorService.handleApiError);
        return response ? response.data.data : [];
    }

    public async getOrganisations() {
        const response = await getAllOrganisations<FilteredOrganisation>({
            filter: 'id;short_name',
            orderBy: 'short_name',
            limit: 0,
        }).catch(ErrorService.handleApiError);
        return response ? response.data.data : [];
    }

    public async created() {
        this.userWatchHandler();
        this.cities = await this.getCities();
        this.citiesDashboard = this.cities.map((item) => ({ ...item }));
        this.roles = await this.getRoles();
        this.projects = await this.getProjects();
        this.organisations = await this.getOrganisations();
    }

    public readonly reduceHandler = (x: any) => x?.id;
}
